import { useCallback, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import { isEqual } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';
import { TextFieldProps, TextFieldVariants } from '@mui/material';
import {
  useEditorStore,
  useFlowBuilder2Context,
  useLayoutComponentSettings,
  getLayoutComponentLabel,
  mutateLayoutComponentLabel,
  LayoutComponentPropertyEditorNames,
  useLayoutComponentPropertyEditorMap,
} from '@diana/core';
import { TextFieldWithValidationTooltip } from '../TextFieldWithValidationTooltip/TextFieldWithValidationTooltip';
import { useEntityFieldState } from '../../Support/useEntityFieldState';

export function ComponentNameField<V extends TextFieldVariants>(
  props: TextFieldProps<V> & {
    layoutId: string;
    componentId: string;
    componentIndex: number;
    autoWidth?: boolean;
    disableValidationResultTooltip?: boolean;
    fontSize?: string;
  },
) {
  const {
    layoutId,
    componentId,
    componentIndex,
    onChange,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    autoWidth,
    disableValidationResultTooltip,
    ...textFieldProps
  } = props;

  const { updateAndSaveLayoutComponentById } = useFlowBuilder2Context();

  const flowId = useEditorStore((s) => s.flow?.id ?? '');
  const layoutComponent = useEditorStore(
    (s) => s.getLayoutComponentById(layoutId, componentId),
    isEqual,
  );
  const displayName = layoutComponent ? getLayoutComponentLabel(layoutComponent) : '';

  const [value, setValue] = useState<string>(displayName);

  const { getSettings } = useLayoutComponentSettings();

  const editorsMap = useLayoutComponentPropertyEditorMap();
  const editor = editorsMap[LayoutComponentPropertyEditorNames.LabelPropertyEditor];
  const editorOptions = useMemo(() => {
    if (layoutComponent) {
      const settings = getSettings(layoutComponent);
      if (settings && settings.editors) {
        for (const editor of settings.editors) {
          if (editor.name === LayoutComponentPropertyEditorNames.LabelPropertyEditor) {
            return editor.options;
          }
        }
      }
    }
    return null;
  }, [getSettings, layoutComponent]);

  const fieldState = useEntityFieldState({
    objectId: componentId,
    objectType: 'layoutComponent',
    fieldName: LayoutComponentPropertyEditorNames.LabelPropertyEditor,
  });

  const validateDisplayName = useCallback(
    (value: string) => {
      if (!layoutComponent || (!fieldState.touched && !value)) return;
      const result = editor.validate({
        flowId,
        layoutId,
        options: editorOptions ?? {},
        component: layoutComponent,
        componentIndex,
      });
      if (!isEqual(fieldState.validationResult, result)) fieldState.setValidationResult(result);
    },
    [editor, editorOptions, fieldState, flowId, layoutComponent, layoutId, componentIndex],
  );

  const saveDisplayName = useDebouncedCallback((displayName: string) => {
    updateAndSaveLayoutComponentById(layoutId, componentId, (draft) => {
      mutateLayoutComponentLabel(draft, displayName);
      return draft;
    });
  }, 500);

  useLayoutEffect(() => {
    if (value !== displayName) setValue(displayName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayName]);

  useEffect(() => {
    return () => saveDisplayName.flush();
  }, [saveDisplayName]);

  return (
    <TextFieldWithValidationTooltip
      error={fieldState.validationResult?.type === 'error'}
      validationResult={disableValidationResultTooltip ? null : fieldState.validationResult}
      {...textFieldProps}
      autoWidth={autoWidth}
      value={value}
      onChange={(e) => {
        const value = e.target.value;
        fieldState.emitDirty();
        setValue(value);
        saveDisplayName(value);
        validateDisplayName(value);
        if (onChange) onChange(e);
      }}
      onBlur={(e) => {
        fieldState.emitTouched();
        validateDisplayName(value);
        if (onBlur) onBlur(e);
      }}
    />
  );
}
